
import { supabase } from '@/integrations/supabase/client';
import { getWebSearchData } from '@/services/webSearchService';
import { getLeadById } from '@/services/leads';
import { getKeyContacts } from '@/services/leads/keyContactService';
import { CompanyWebSearchResult, getCompanyWebSearchData } from '@/services/companyWebSearchService';
import { getStatusDisplayInfo } from '@/services/leads/leadStatus';
import { getUserCompanyId } from './core';
import { LeadStatus } from '@/types/lead';

interface SenderInfo {
  senderName?: string;
  senderRole?: string;
  senderCompany?: string;
  senderEmail?: string;
  senderPhone?: string;
  senderContactNumber?: string;
}

export const generateCallScript = async (
  leadId: string,
  senderInfo?: SenderInfo,
  customPrompt?: string
): Promise<string | null> => {
  try {
    // Get lead data
    const lead = await getLeadById(leadId);
    if (!lead) {
      console.error('Lead not found:', leadId);
      return null;
    }

    // Get lead web search data and key contacts
    const [leadWebSearchData, keyContacts] = await Promise.all([
      getWebSearchData(leadId),
      getKeyContacts(leadId)
    ]);

    // Get user's company ID
    const companyId = await getUserCompanyId();
    
    // Get company web search data if company ID exists
    let companyWebSearchData: CompanyWebSearchResult | null = null;
    let outreachInstructions: string | null = null;
    let companyWebsite: string | null = null;
    if (companyId) {
      companyWebSearchData = await getCompanyWebSearchData(companyId);
      const { data: companyRow } = await supabase
        .from('companies')
        .select('outreach_instructions, website')
        .eq('id', companyId)
        .single();
      outreachInstructions = (companyRow as any)?.outreach_instructions || null;
      companyWebsite = (companyRow as any)?.website || null;
    }
    
    // Get lead status info for personalized templates
    const leadStatus = (lead.status || 'new') as LeadStatus;
    const statusInfo = getStatusDisplayInfo(leadStatus);

    // Validate sender info
    const validatedSenderInfo: Record<string, string> = {
      senderName: senderInfo?.senderName?.trim() || '',
      senderRole: senderInfo?.senderRole?.trim() || '',
      senderCompany: senderInfo?.senderCompany?.trim() || '',
    };
    if (companyWebsite) {
      validatedSenderInfo.senderWebsite = companyWebsite;
    }
    
    console.log('Generating call script with sender info:', validatedSenderInfo);

    // Call edge function to generate the call script
    const { data, error } = await supabase.functions.invoke('generate-template', {
      body: { 
        type: 'call',
        leadData: {
          ...lead,
          status: leadStatus,
          statusLabel: statusInfo.label,
          webSearchData: leadWebSearchData,
          keyContacts: keyContacts || []
        },
        companyData: { ...companyWebSearchData, outreachInstructions },
        templateType: leadStatus || 'default',
        senderInfo: validatedSenderInfo,
        customPrompt: customPrompt || ''
      }
    });

    if (error) {
      console.error('Error generating call script:', error);
      return null;
    }

    return data.template;
  } catch (error) {
    console.error('Error in generateCallScript:', error);
    return null;
  }
};
